import React, { useState, useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button,
  Spinner,
  Input,
  FormGroup,
  Label,
  Alert
} from 'reactstrap';
import { useAuth } from '../../../contexts/AuthContext';
import dealService from '../../../service/dealService.ts';
import apiService from '../../../service/apiService';
import { formatNumber } from '../../../utils/formatUtils';

const ScreenerTab = ({ 
  deal, 
  dealPublicId, 
  handleUnderwrite, 
  underwriteLoading 
}) => {
  const { getToken } = useAuth();
  
  // Wrapper function to debug button clicks
  const handleUnderwriteClick = () => {
    console.log('[ScreenerTab] *** UNDERWRITE BUTTON CLICKED IN SCREENER TAB ***');
    console.log('[ScreenerTab] Button click timestamp:', new Date().toISOString());
    console.log('[ScreenerTab] About to call handleUnderwrite...');
    
    if (typeof handleUnderwrite === 'function') {
      console.log('[ScreenerTab] handleUnderwrite is a function, calling it now...');
      handleUnderwrite();
      console.log('[ScreenerTab] handleUnderwrite called successfully');
    } else {
      console.error('[ScreenerTab] ERROR: handleUnderwrite is not a function!');
    }
  };
  
  // Estado para los campos editables
  const [isEditing, setIsEditing] = useState(false);
  const [totalSubscribers, setTotalSubscribers] = useState(deal?.totalSubscribers || 0);
  const [totalMonthlyRevenue, setTotalMonthlyRevenue] = useState(deal?.totalMonthlyRevenue || 0);
  const [originalTotalSubscribers, setOriginalTotalSubscribers] = useState(deal?.totalSubscribers || 0);
  const [originalTotalMonthlyRevenue, setOriginalTotalMonthlyRevenue] = useState(deal?.totalMonthlyRevenue || 0);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateError, setUpdateError] = useState(null);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  
  // Estado para declined reason
  const [declinedReason, setDeclinedReason] = useState(null);
  const [declinedReasonLoading, setDeclinedReasonLoading] = useState(false);

  // Estado para monitoring center
  const [monitoringCenterDetails, setMonitoringCenterDetails] = useState(null);
  const [monitoringCenterLoading, setMonitoringCenterLoading] = useState(false);
  const [monitoringCenterError, setMonitoringCenterError] = useState(null);

  // Efecto para obtener declined reason si el deal está declined
  useEffect(() => {
    const fetchDeclinedReason = async () => {
      if (deal?.status === 'DECLINED' && deal?.privateId) {
        try {
          setDeclinedReasonLoading(true);
          const token = getToken();
          if (token) {
            const response = await dealService.getDeclinedReason(token, deal.privateId);
            setDeclinedReason(response.data.reason);
          }
        } catch (error) {
          console.error('Error fetching declined reason:', error);
          // No mostrar error al usuario, simplemente no mostrar el reason
        } finally {
          setDeclinedReasonLoading(false);
        }
      }
    };

    fetchDeclinedReason();
  }, [deal?.status, deal?.privateId, getToken]);

  // Efecto para obtener detalles del monitoring center
  useEffect(() => {
    const fetchMonitoringCenterDetails = async () => {
      // Use merchantId to get monitoring center details
      if (deal?.merchantId) {
        try {
          setMonitoringCenterLoading(true);
          setMonitoringCenterError(null);
          
          // Get monitoring center details using the merchantId
          const details = await apiService.getMonitoringCenterById(deal.merchantId);
          setMonitoringCenterDetails(details);
        } catch (error) {
          console.error('Error fetching monitoring center details:', error);
          setMonitoringCenterError('Failed to load monitoring center details');
        } finally {
          setMonitoringCenterLoading(false);
        }
      }
    };

    fetchMonitoringCenterDetails();
  }, [deal?.merchantId]);

  // Función para manejar la edición
  const handleEdit = () => {
    // Guardar los valores actuales como originales antes de editar
    setOriginalTotalSubscribers(totalSubscribers);
    setOriginalTotalMonthlyRevenue(totalMonthlyRevenue);
    setIsEditing(true);
    setUpdateError(null);
    setUpdateSuccess(false);
  };

  // Función para cancelar la edición
  const handleCancel = () => {
    setIsEditing(false);
    // Restaurar los valores originales (los que estaban antes de hacer click en Edit)
    setTotalSubscribers(originalTotalSubscribers);
    setTotalMonthlyRevenue(originalTotalMonthlyRevenue);
    setUpdateError(null);
    setUpdateSuccess(false);
  };

  // Función para guardar los cambios
  const handleSave = async () => {
    try {
      setUpdateLoading(true);
      setUpdateError(null);
      
      // Obtener token usando el hook useAuth
      const token = getToken();
      if (!token) {
        throw new Error('Authentication token not available');
      }

      // Validar que el deal tenga privateId
      if (!deal || !deal.privateId) {
        throw new Error('Deal private ID not available');
      }

      // Preparar el payload con todos los campos del deal
      const payload = {
        publicId: deal.publicId,
        requestedAmount: deal.requestedAmount,
        totalSubscribers: parseInt(totalSubscribers) || 0,
        totalMonthlyRevenue: parseInt(totalMonthlyRevenue) || 0,
        legalName: deal.legalName,
        dbaName: deal.dbaName,
        federalTaxId: deal.federalTaxId,
        stateOfInc: deal.stateOfInc,
        industry: deal.industry,
        businessStructure: deal.businessStructure,
        flowType: deal.flowType || "SUP",
        status: deal.status,
        owners: deal.owners,
        bankAccounts: deal.bankAccounts,
        // Preserve monitoring center data
        merchantId: deal.merchantId,
        monitoringCenter: deal.monitoringCenter
      };

      // Realizar la petición PUT
      await dealService.updateDeal(token, deal.privateId, payload);
      
      setIsEditing(false);
      setUpdateSuccess(true);
      
      // Guardar la tab activa antes de recargar para mantenerla activa después del reload
      localStorage.setItem('activeTabAfterSave', '2'); // ScreenerTab es la tab '2'
      
      // Recargar la página después de un breve delay para mostrar el mensaje de éxito
      setTimeout(() => {
        window.location.reload();
      }, 1500);

    } catch (error) {
      console.error('Error updating deal:', error);
      setUpdateError(error.message || 'Error updating data');
    } finally {
      setUpdateLoading(false);
    }
  };

  return (
    <Row>
      <Col>
        {/* Business Metrics Section */}
        <Card className="shadow-sm">
          <CardHeader className="bg-info text-white">
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <i className="ni ni-chart-bar-32 mr-2"></i>
                <h5 className="mb-0">Business Metrics</h5>
              </div>
              {!isEditing ? (
                deal?.status === 'SCREENER' && (
                  <Button 
                    color="light" 
                    size="sm" 
                    onClick={handleEdit}
                    disabled={updateLoading}
                  >
                    <i className="ni ni-ruler-pencil mr-1"></i>
                    Edit
                  </Button>
                )
              ) : (
                <div>
                  <Button 
                    color="success" 
                    size="sm" 
                    onClick={handleSave}
                    disabled={updateLoading}
                    className="mr-2"
                  >
                    {updateLoading ? (
                      <>
                        <Spinner size="sm" className="mr-1" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="ni ni-check-bold mr-1"></i>
                        Save
                      </>
                    )}
                  </Button>
                  <Button 
                    color="secondary" 
                    size="sm" 
                    onClick={handleCancel}
                    disabled={updateLoading}
                  >
                    <i className="ni ni-fat-remove mr-1"></i>
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardBody className="py-2">
            {updateError && (
              <Alert color="danger" className="mb-2">
                <i className="ni ni-support-16 mr-2"></i>
                {updateError}
              </Alert>
            )}
            {updateSuccess && (
              <Alert color="success" className="mb-2">
                <i className="ni ni-check-bold mr-2"></i>
                Data updated successfully
              </Alert>
            )}
            
            <Row>
              <Col md="6">
                <FormGroup className="mb-3">
                  <Label for="totalSubscribers">
                    <strong>Total Subscribers</strong>
                  </Label>
                  <Input
                    type="number"
                    id="totalSubscribers"
                    value={totalSubscribers}
                    onChange={(e) => setTotalSubscribers(e.target.value)}
                    disabled={!isEditing}
                    placeholder="Enter total number of subscribers"
                    className={isEditing ? '' : 'bg-light'}
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup className="mb-2">
                  <Label for="totalMonthlyRevenue">
                    <strong>Total Monthly Revenue</strong>
                  </Label>
                  <Input
                    type="number"
                    id="totalMonthlyRevenue"
                    value={totalMonthlyRevenue}
                    onChange={(e) => setTotalMonthlyRevenue(e.target.value)}
                    disabled={!isEditing}
                    placeholder="Enter total monthly revenue"
                    className={isEditing ? '' : 'bg-light'}
                  />
                </FormGroup>
              </Col>
            </Row>

            {/* Monitoring Center Contact Information */}
            {deal?.merchantId && (
              <>
                <hr className="my-3" />
                <div className="mb-3">
                  <div className="d-flex align-items-center mb-2">
                    <i className="ni ni-building text-info mr-2" style={{ fontSize: '1.5rem' }}></i>
                    <h5 className="mb-0 text-dark font-weight-bold">Monitoring Center Contact</h5>
                  </div>
                  
                  {monitoringCenterLoading ? (
                    <div className="text-center py-2">
                      <Spinner color="primary" size="sm" />
                      <span className="ml-2 text-muted">Loading contact information...</span>
                    </div>
                  ) : monitoringCenterError ? (
                    <Alert color="warning" className="mb-0">
                      <i className="ni ni-support-16 mr-2"></i>
                      {monitoringCenterError}
                    </Alert>
                    ) : monitoringCenterDetails ? (
                      <>
                        {/* Company Name + First Owner Contact Row */}
                        {(() => {
                          const firstOwner = monitoringCenterDetails.owners?.[0];
                          const firstPrimaryPhone = firstOwner?.contacts?.find(c => c.type === 'PHONE' && c.isPrimary === true && c.deleted === false);
                          const firstPrimaryEmail = firstOwner?.contacts?.find(c => c.type === 'EMAIL' && c.isPrimary === true && c.deleted === false);
                          
                          return (
                            <Row className="mb-2">
                              {/* Company */}
                              <Col md="4">
                                <FormGroup className="mb-2">
                                  <Label className="text-muted">
                                    <strong>Company</strong>
                                  </Label>
                                  <div className="bg-light rounded p-2 text-muted">
                                    {monitoringCenterDetails.legalName || 'N/A'}
                                  </div>
                                </FormGroup>
                              </Col>
                              
                              {/* First Owner Phone */}
                              <Col md="4">
                                <FormGroup className="mb-2">
                                  <Label className="text-muted">
                                    <strong>Phone</strong>
                                  </Label>
                                  <div className="bg-light rounded p-2 text-muted">
                                    {firstPrimaryPhone ? (
                                      <a className="text-dark" style={{ textDecoration: 'none' }}>
                                        <i className="text-primary mr-2"></i>
                                        {firstPrimaryPhone.value}
                                      </a>
                                    ) : (
                                      'N/A'
                                    )}
                                  </div>
                                </FormGroup>
                              </Col>
                              
                              {/* First Owner Email */}
                              <Col md="4">
                                <FormGroup className="mb-2">
                                  <Label className="text-muted">
                                    <strong>Email</strong>
                                  </Label>
                                  <div className="bg-light rounded p-2 text-muted">
                                    {firstPrimaryEmail ? (
                                      <a className="text-dark" style={{ textDecoration: 'none' }}>
                                        <i className="text-primary mr-2"></i>
                                        {firstPrimaryEmail.value}
                                      </a>
                                    ) : (
                                      'N/A'
                                    )}
                                  </div>
                                </FormGroup>
                              </Col>
                            </Row>
                          );
                        })()}
                        
                        {/* Additional Owners Contact Information - Show only primary contacts */}
                        {monitoringCenterDetails.owners && monitoringCenterDetails.owners.length > 1 ? (
                          monitoringCenterDetails.owners.slice(1).map((owner, ownerIndex) => {
                            // Filter only primary contacts
                            const primaryPhone = owner?.contacts?.find(c => c.type === 'PHONE' && c.isPrimary === true && c.deleted === false);
                            const primaryEmail = owner?.contacts?.find(c => c.type === 'EMAIL' && c.isPrimary === true && c.deleted === false);
                            
                            // Only show owners that have at least one primary contact
                            if (!primaryPhone && !primaryEmail) {
                              return null;
                            }
                            
                            return (
                              <Row key={ownerIndex + 1} className="mb-1">
                                {/* Empty column for alignment with Company */}
                                <Col md="4">
                                </Col>
                                
                                {/* Phone */}
                                <Col md="4">
                                  <FormGroup className="mb-1">
                                    <div className="bg-light rounded p-2 text-muted">
                                      {primaryPhone ? (
                                        <a className="text-dark" style={{ textDecoration: 'none' }}>
                                          <i className="text-primary mr-2"></i>
                                          {primaryPhone.value}
                                        </a>
                                      ) : (
                                        'N/A'
                                      )}
                                    </div>
                                  </FormGroup>
                                </Col>
                                
                                {/* Email */}
                                <Col md="4">
                                  <FormGroup className="mb-1">
                                    <div className="bg-light rounded p-2 text-muted">
                                      {primaryEmail ? (
                                        <a className="text-dark" style={{ textDecoration: 'none' }}>
                                          <i className="text-primary mr-2"></i>
                                          {primaryEmail.value}
                                        </a>
                                      ) : (
                                        'N/A'
                                      )}
                                    </div>
                                  </FormGroup>
                                </Col>
                              </Row>
                            );
                          })
                        ) : null}
                      </>
                    ) : (
                    <Alert color="info" className="mb-0">
                      <i className="ni ni-info-circle mr-2"></i>
                      No monitoring center contact information available
                    </Alert>
                  )}
                </div>
              </>
            )}

            {/* Underwriting Section - Integrado en la misma card */}
            <hr className="my-4" />
            <div className="text-center">
              {deal?.status !== 'SCREENER' && deal?.status !== 'DECLINED' ? (
                // Deal was pre-approved
                <div>
                  <div className="mb-3">
                    <i className="ni ni-check-bold text-success" style={{ fontSize: '2rem' }}></i>
                  </div>
                  <div className="alert alert-success" role="alert">
                    <i className="fas fa-check-circle mr-2"></i>
                    Congratulations, your deal application has been pre-approved
                  </div>
                </div>
              ) : deal?.status === 'DECLINED' ? (
                // Deal was declined
                <div>
                  <div className="mb-1">
                    <i className="ni ni-fat-remove text-danger" style={{ fontSize: '2rem' }}></i>
                  </div>
                  <div className="alert alert-danger" role="alert">
                    <i className="fas fa-exclamation-triangle mr-2"></i>
                    Sorry, your deal application has been declined
                    {declinedReasonLoading ? (
                      <div className="mt-1">
                        <Spinner size="sm" className="mr-2" />
                        Loading reason...
                      </div>
                    ) : declinedReason ? (
                      <div className="mt-1">
                        <strong>Reason:</strong> {declinedReason}
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : (
                // Deal is ready for underwriting
                <div>
                  <div className="mb-1">
                    <i className="ni ni-check-bold text-warning" style={{ fontSize: '2rem' }}></i>
                  </div>
                  <p className="text-muted mb-4">Ready to evaluate this deal for underwriting approval.</p>
                  <Button 
                    color="warning"
                    size="lg"
                    onClick={handleUnderwriteClick}
                    disabled={underwriteLoading || isEditing}
                    className="px-5"
                  >
                    {underwriteLoading ? (
                      <>
                        <Spinner size="sm" className="mr-2" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <i className="ni ni-check-bold mr-2"></i>
                        Underwrite
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default ScreenerTab;